import { Loader2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Document, Page, pdfjs } from 'react-pdf'
import { isMobileViewport } from '@/hooks/use-mobile'
import { cn } from '@/utils/cn'

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url
).toString()

/** Whole-file mode shows this many pages, then this many more per step. */
const PAGE_BATCH = 3

/**
 * Whole-file paging: the next batch is added when the last page shown scrolls into
 * view. The observer is armed only once that page has been drawn, so pages that have
 * no height yet cannot set off a cascade.
 */
function usePageReveal(pageCount: number) {
  const [revealed, setRevealed] = useState(PAGE_BATCH)
  const [drawnThrough, setDrawnThrough] = useState(0)
  const lastPageRef = useRef<HTMLDivElement>(null)
  const shownCount = Math.min(revealed, pageCount)
  const armed = shownCount < pageCount && drawnThrough >= shownCount

  useEffect(() => {
    const lastPage = lastPageRef.current
    if (!armed || !lastPage) return
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) setRevealed(n => n + PAGE_BATCH)
    })
    observer.observe(lastPage)
    return () => observer.disconnect()
  }, [armed])

  const markDrawn = (page: number) => setDrawnThrough(n => Math.max(n, page))
  return {
    shownCount,
    pageProps: (page: number) => ({
      inputRef: page === shownCount ? lastPageRef : undefined,
      onRenderSuccess: () => markDrawn(page),
      onRenderError: () => markDrawn(page),
    }),
  }
}

interface PdfPageStackProps {
  documentUrl: string
  /** 1-based page numbers to show. Omit for the whole file, which is revealed a few
   *  pages at a time so a long document never holds a canvas for every page. */
  pages?: number[]
  className?: string
}

/** The pages of a PDF drawn by react-pdf, stacked in a scrolling frame. */
export function PdfPageStack({ documentUrl, pages, className }: PdfPageStackProps) {
  const file = useMemo(() => ({ url: documentUrl }), [documentUrl])
  const [pageCount, setPageCount] = useState(0)
  const reveal = usePageReveal(pages ? 0 : pageCount)
  const shown = pages ?? Array.from({ length: reveal.shownCount }, (_, i) => i + 1)
  // The phone scales the 720 px bitmap down to the pane, where it is already 2x.
  const devicePixelRatio = isMobileViewport() ? 1 : undefined

  return (
    <div className={cn('flex-1 overflow-auto border border-border rounded bg-card', className)}>
      <Document
        file={file}
        onLoadSuccess={pdf => setPageCount(pdf.numPages)}
        loading={
          <div className="flex items-center justify-center p-8 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin mr-2" />
            Loading PDF…
          </div>
        }
        error={
          <div className="p-6 text-center text-sm text-destructive">
            Failed to load PDF preview.
          </div>
        }
      >
        {/* Pages render at a fixed 720px; the phone scales their canvas to the pane. */}
        <div className="flex flex-col items-center gap-3 p-3 max-md:items-stretch max-md:[&_canvas]:!h-auto max-md:[&_canvas]:!w-full">
          {shown.map(p => (
            <Page
              key={p}
              pageNumber={p}
              width={720}
              devicePixelRatio={devicePixelRatio}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              className="shadow-sm"
              {...(pages ? undefined : reveal.pageProps(p))}
            />
          ))}
        </div>
      </Document>
    </div>
  )
}
